import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { CircleCheck, CircleX, Loader2, PackageCheck, ClipboardCheck } from 'lucide-react';

import BarcodeScannerEntryField from '../../shared/BarcodeScannerEntryField';
import useBarcodeScanner from '../../shared/useBarcodeScanner';
import ScanStepBreadcrumb from '../../shared/ScanStepBreadcrumb';
import type { ScannerEntryMode } from '../../shared/types/ScannerTypes';
import {
  looksLikeRacerangerDeviceScanAttempt,
  NOT_RACERANGER_DEVICE_SCAN_TOAST,
  maybeConvertRacerangerScan,
  resolveRacerangerScannedDeviceId,
} from '../../../helpers/racerangerDeviceScan';
import { dispatchToast } from '../utils/eventsOverviewHelpers';
import { isFrontDeviceId, isRearDeviceId } from '../../../helpers/devicePairing';
import {
  buildDeviceMappingIdSet,
  validateRearDeviceMappingAndFormat,
} from '../utils/rearDevicePackagingValidation';
import {
  buildDistributionMasterRows,
  canEditDistributionAssignments,
  type DistributionMasterRow,
} from '../utils/distributionMasterRows';
import type { EventDeviceRow } from '../types';

type ScanTarget = 'collection' | 'racking';
type RackingPhase = 'bib' | 'rear' | 'front';
type RowMismatch = { rearMatches?: boolean; frontMatches?: boolean };

type Props = {
  eventId: string;
  raceId: string;
  raceStatus: string;
  combinedMode: boolean;
  devices: EventDeviceRow[];
  onRefresh?: (raceId?: string) => void | Promise<void>;
};

const CELL_MONO = 'font-mono text-xs';
const RACKED_CELL = `${CELL_MONO} rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200`;
const PLAIN_CELL = `${CELL_MONO} text-gray-900 dark:text-slate-100`;

function TickOrCross({ ok, loading }: { ok: boolean; loading?: boolean }): JSX.Element {
  if (loading) {
    return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500 dark:text-blue-400" aria-label="Checking…" />;
  }
  return ok ? (
    <CircleCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} aria-label="Yes" />
  ) : (
    <CircleX className="h-4 w-4 shrink-0 text-gray-300 dark:text-slate-600" strokeWidth={2.5} aria-label="No" />
  );
}

const RACKING_STEPS: { key: RackingPhase; label: string }[] = [
  { key: 'bib', label: '1 Bib' },
  { key: 'rear', label: '2 Rear' },
  { key: 'front', label: '3 Front' },
];

/**
 * Distribution + Racking master view: one table of athlete/device pairs with
 * collected and racked status, plus a single scanner toggled between marking
 * collection (scan the bib QR on the packet) and running racking verification
 * (scan bib to select, then rear, then front — each device goes green on its
 * own match). Replaces the separate combined-collection section and Racking
 * verification dialog, which tracked the same two device-lifecycle flags in
 * two different places.
 */
export default function DistributionMasterTable(props: Props): JSX.Element {
  const { eventId, raceId, raceStatus, combinedMode, devices, onRefresh } = props;

  const rows = useMemo(() => buildDistributionMasterRows(devices), [devices]);
  const canEdit = canEditDistributionAssignments(raceStatus, combinedMode);

  const [scanTarget, setScanTarget] = useState<ScanTarget>('collection');
  const [scanValue, setScanValue] = useState('');
  const [scanMode, setScanMode] = useState<ScannerEntryMode>('scan');
  const [busy, setBusy] = useState(false);
  const [pendingCell, setPendingCell] = useState<{ key: string; field: 'collected' | 'racked' } | null>(null);

  const [rackingPhase, setRackingPhase] = useState<RackingPhase>('bib');
  const [activeAthleteId, setActiveAthleteId] = useState<string | null>(null);
  const [activeRaceNo, setActiveRaceNo] = useState<string | null>(null);
  const [mismatchByAthleteId, setMismatchByAthleteId] = useState<Record<string, RowMismatch>>({});
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const [editingAthleteId, setEditingAthleteId] = useState<string | null>(null);
  const [editRear, setEditRear] = useState('');
  const [editFront, setEditFront] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [masterIds, setMasterIds] = useState<Set<string> | null | undefined>(undefined);

  const [showAddAthlete, setShowAddAthlete] = useState(false);
  const [newAthleteBib, setNewAthleteBib] = useState('');
  const [newAthleteName, setNewAthleteName] = useState('');
  const [newAthleteRear, setNewAthleteRear] = useState('');
  const [newAthleteFront, setNewAthleteFront] = useState('');
  const [addingAthlete, setAddingAthlete] = useState(false);
  const [addAthletePhase, setAddAthletePhase] = useState<RackingPhase>('bib');
  const [addAthleteScanValue, setAddAthleteScanValue] = useState('');
  const [addAthleteScanMode, setAddAthleteScanMode] = useState<ScannerEntryMode>('scan');

  const collectedCount = rows.filter((r) => r.collected).length;
  const rackedCount = rows.filter((r) => r.racked).length;

  const resetRackingScan = (): void => {
    setActiveAthleteId(null);
    setActiveRaceNo(null);
    setRackingPhase('bib');
    setHighlightedRowKey(null);
  };

  const persistDeviceRacked = useCallback(
    async (deviceId: string, label: string): Promise<boolean> => {
      if (!window.eventsAPI?.setRaceDevicesRacked) {
        dispatchToast('toast-error', 'Set racked API unavailable.');
        return false;
      }
      setBusy(true);
      try {
        const res = await window.eventsAPI.setRaceDevicesRacked(eventId, raceId, {
          deviceIds: [deviceId],
          racked: 1,
        });
        if (!res?.ok) {
          const rawError = String(res?.error ?? '');
          dispatchToast(
            'toast-error',
            rawError.includes('device_not_collected')
              ? `${label} has not been collected yet — scan it as collected first.`
              : res?.error || `Failed to persist racked status for ${deviceId}.`,
          );
          return false;
        }
        await Promise.resolve(onRefresh?.(raceId));
        return true;
      } finally {
        setBusy(false);
      }
    },
    [eventId, raceId, onRefresh],
  );

  const applyCollectionScan = useCallback(
    async (raw: string): Promise<void> => {
      const value = String(raw ?? '').trim();
      if (!value) return;

      if (looksLikeRacerangerDeviceScanAttempt(value)) {
        dispatchToast('toast-error', 'Scan the bib QR code on the packet, not a device QR.');
        return;
      }
      const row = rows.find((r) => r.athleteRaceNo && r.athleteRaceNo === value);
      if (!row) {
        dispatchToast('toast-error', `No athlete with race number "${value}".`);
        return;
      }
      setHighlightedRowKey(row.athleteId || row.rearDeviceId);
      if (row.collected) {
        dispatchToast('toast-error', `Bib ${value} (${row.athleteName || row.rearDeviceId}) is already marked collected.`);
        return;
      }
      const ids = [row.rearDeviceId, row.frontDeviceId].filter(Boolean);
      if (!window.eventsAPI?.setRaceDevicesDistributed) {
        dispatchToast('toast-error', 'Collection API unavailable.');
        return;
      }
      setBusy(true);
      setPendingCell({ key: row.athleteId || row.rearDeviceId, field: 'collected' });
      try {
        const res = await window.eventsAPI.setRaceDevicesDistributed(eventId, raceId, {
          deviceIds: ids,
          distributed: 1,
        });
        if (!res?.ok) {
          dispatchToast('toast-error', String(res?.error ?? `Failed to mark bib ${value} collected.`));
          return;
        }
        setScanValue('');
        await Promise.resolve(onRefresh?.(raceId));
      } finally {
        setBusy(false);
        setPendingCell(null);
      }
    },
    [rows, eventId, raceId, onRefresh],
  );

  const undoCollection = useCallback(
    async (row: DistributionMasterRow): Promise<void> => {
      if (row.rearRacked || row.frontRacked) {
        dispatchToast('toast-error', `${row.athleteName || row.rearDeviceId} is already racked; un-rack it before undoing collection.`);
        return;
      }
      const ids = [row.rearDeviceId, row.frontDeviceId].filter(Boolean);
      if (!window.eventsAPI?.setRaceDevicesDistributed) return;
      setBusy(true);
      try {
        const res = await window.eventsAPI.setRaceDevicesDistributed(eventId, raceId, {
          deviceIds: ids,
          distributed: 0,
        });
        if (!res?.ok) {
          dispatchToast('toast-error', String(res?.error ?? `Failed to undo collection for ${row.rearDeviceId}.`));
          return;
        }
        await Promise.resolve(onRefresh?.(raceId));
      } finally {
        setBusy(false);
      }
    },
    [eventId, raceId, onRefresh],
  );

  const undoRacking = useCallback(
    async (row: DistributionMasterRow): Promise<void> => {
      const ids = [row.rearDeviceId, row.frontDeviceId].filter(Boolean);
      if (!window.eventsAPI?.setRaceDevicesRacked) return;
      setBusy(true);
      try {
        const res = await window.eventsAPI.setRaceDevicesRacked(eventId, raceId, {
          deviceIds: ids,
          racked: 0,
        });
        if (!res?.ok) {
          dispatchToast('toast-error', String(res?.error ?? `Failed to undo racking for ${row.rearDeviceId}.`));
          return;
        }
        await Promise.resolve(onRefresh?.(raceId));
      } finally {
        setBusy(false);
      }
    },
    [eventId, raceId, onRefresh],
  );

  const applyRackingScan = useCallback(
    async (raw: string): Promise<void> => {
      const value = String(raw ?? '').trim();
      if (!value) return;

      if (rackingPhase === 'bib') {
        if (looksLikeRacerangerDeviceScanAttempt(value)) {
          dispatchToast('toast-error', 'Scan the athlete bib number first, then rear and front devices.');
          return;
        }
        const row = rows.find((r) => r.athleteRaceNo && r.athleteRaceNo === value);
        if (!row) {
          dispatchToast('toast-error', `No athlete with race number "${value}".`);
          return;
        }
        setActiveAthleteId(row.athleteId);
        setActiveRaceNo(row.athleteRaceNo);
        setHighlightedRowKey(row.athleteId || row.rearDeviceId);
        setRackingPhase('rear');
        setScanValue('');
        return;
      }

      const row = rows.find((r) => r.athleteId === activeAthleteId);
      if (!row) {
        resetRackingScan();
        return;
      }

      const role = rackingPhase === 'rear' ? 'rear' : 'front';
      const resolved = resolveRacerangerScannedDeviceId(value, role);
      const scanned = resolved.ok ? resolved.deviceId : value;
      if (!resolved.ok && looksLikeRacerangerDeviceScanAttempt(value)) {
        dispatchToast('toast-error', NOT_RACERANGER_DEVICE_SCAN_TOAST);
        return;
      }

      if (rackingPhase === 'rear') {
        const expected = row.rearDeviceId;
        const matches = expected.trim().toLowerCase() === scanned.trim().toLowerCase();
        setMismatchByAthleteId((prev) => ({ ...prev, [row.athleteId]: { ...prev[row.athleteId], rearMatches: matches } }));
        if (!matches) {
          dispatchToast('toast-error', `Mismatch on bib ${row.athleteRaceNo}: expected rear "${expected}", scanned "${scanned}".`);
        } else {
          setPendingCell({ key: row.athleteId || row.rearDeviceId, field: 'racked' });
          await persistDeviceRacked(row.rearDeviceId, `Bib ${row.athleteRaceNo} rear`);
          setPendingCell(null);
        }
        setRackingPhase('front');
        setScanValue('');
        return;
      }

      const expectedFront = row.frontDeviceId;
      const matches = !!expectedFront && expectedFront.trim().toLowerCase() === scanned.trim().toLowerCase();
      setMismatchByAthleteId((prev) => ({ ...prev, [row.athleteId]: { ...prev[row.athleteId], frontMatches: matches } }));
      if (!expectedFront) {
        dispatchToast('toast-error', `${row.athleteName || row.rearDeviceId} has no front device assigned.`);
      } else if (!matches) {
        dispatchToast('toast-error', `Mismatch on bib ${row.athleteRaceNo}: expected front "${expectedFront}", scanned "${scanned}".`);
      } else {
        setPendingCell({ key: row.athleteId || row.rearDeviceId, field: 'racked' });
        await persistDeviceRacked(row.frontDeviceId, `Bib ${row.athleteRaceNo} front`);
        setPendingCell(null);
      }
      resetRackingScan();
      setScanValue('');
    },
    [rackingPhase, activeAthleteId, rows, persistDeviceRacked],
  );

  const { connectionActive, claim: claimScanner } = useBarcodeScanner({
    enabled: rows.length > 0,
    onScan: (value) => {
      // Show the raw scan immediately so the field never looks unresponsive,
      // even though the readOnly "scan" input is otherwise only driven by us.
      setScanValue(value);
      void (effectiveScanTarget === 'collection' ? applyCollectionScan(value) : applyRackingScan(value));
    },
  });

  useEffect(() => {
    if (!highlightedRowKey || !tableRef.current) return;
    const index = rows.findIndex((r) => (r.athleteId || r.rearDeviceId) === highlightedRowKey);
    if (index < 0) return;
    const rowEl = tableRef.current.querySelector(`[data-row-index="${index}"]`);
    rowEl?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [highlightedRowKey, rows]);

  const loadMasterIds = useCallback(async (): Promise<Set<string> | null> => {
    const api = window.eventsAPI;
    if (!api?.getDeviceMappings) {
      setMasterIds(null);
      return null;
    }
    try {
      const res = await api.getDeviceMappings();
      if (!res?.ok) {
        setMasterIds(null);
        return null;
      }
      const rowsRaw = (res.data as { devices?: unknown } | undefined)?.devices;
      const deviceRows = Array.isArray(rowsRaw) ? (rowsRaw as Record<string, string>[]) : [];
      const next = buildDeviceMappingIdSet(deviceRows);
      setMasterIds(next);
      return next;
    } catch {
      setMasterIds(null);
      return null;
    }
  }, []);

  const beginEdit = (row: DistributionMasterRow): void => {
    if (!row.athleteId) return;
    setEditingAthleteId(row.athleteId);
    setEditRear(row.rearDeviceId);
    setEditFront(row.frontDeviceId);
  };

  const cancelEdit = (): void => {
    setEditingAthleteId(null);
    setEditRear('');
    setEditFront('');
    setSavingEdit(false);
  };

  const saveEdit = useCallback(async (): Promise<void> => {
    const row = rows.find((r) => r.athleteId === editingAthleteId);
    if (!row) return;

    const rear = maybeConvertRacerangerScan(editRear).trim();
    const front = maybeConvertRacerangerScan(editFront).trim();
    if (!rear) {
      dispatchToast('toast-error', 'Rear device ID is required.');
      return;
    }
    const masterSet = masterIds === undefined ? await loadMasterIds() : masterIds;
    const check = validateRearDeviceMappingAndFormat(rear, masterSet ?? null);
    if (!check.ok) {
      dispatchToast('toast-error', check.reason ?? 'Rear device validation failed');
      return;
    }
    if (!window.eventsAPI?.updateRaceAthleteAssignment) {
      dispatchToast('toast-error', 'Update assignment API unavailable.');
      return;
    }

    setSavingEdit(true);
    try {
      const res = await window.eventsAPI.updateRaceAthleteAssignment(eventId, raceId, {
        athleteId: row.athleteId,
        rearDeviceId: rear,
        frontDeviceId: front || undefined,
        athleteName: row.athleteName || undefined,
        athleteRaceNo: row.athleteRaceNo || undefined,
        verificationEditedAt: new Date().toISOString(),
        verificationEditedStage: raceStatus,
        verificationRearFrom: row.rearDeviceId,
        verificationRearTo: rear,
        verificationFrontFrom: row.frontDeviceId,
        verificationFrontTo: front,
      });
      if (!res?.ok) {
        const rawError = String(res?.error ?? '');
        dispatchToast(
          'toast-error',
          rawError.includes('device_assignment_conflict')
            ? 'One of those devices is already assigned to another athlete.'
            : res?.error || 'Failed to save assignment update.',
        );
        return;
      }
      dispatchToast('toast-info', `Updated devices for ${row.athleteName || row.athleteId}`);
      cancelEdit();
      await Promise.resolve(onRefresh?.(raceId));
    } finally {
      setSavingEdit(false);
    }
  }, [rows, editingAthleteId, editRear, editFront, masterIds, loadMasterIds, eventId, raceId, raceStatus, onRefresh]);

  const resetAddAthleteFields = (): void => {
    setNewAthleteBib('');
    setNewAthleteName('');
    setNewAthleteRear('');
    setNewAthleteFront('');
    setAddAthletePhase('bib');
    setAddAthleteScanValue('');
  };

  const cancelAddAthlete = (): void => {
    setShowAddAthlete(false);
    resetAddAthleteFields();
    setAddingAthlete(false);
  };

  const applyAddAthleteScan = useCallback(
    (raw: string): void => {
      const value = String(raw ?? '').trim();
      if (!value) return;

      if (addAthletePhase === 'bib') {
        if (looksLikeRacerangerDeviceScanAttempt(value)) {
          dispatchToast('toast-error', 'Scan the athlete bib number first, then rear and front devices.');
          return;
        }
        setNewAthleteBib(value);
        setAddAthletePhase('rear');
        setAddAthleteScanValue('');
        return;
      }

      const role = addAthletePhase === 'rear' ? 'rear' : 'front';
      const resolved = resolveRacerangerScannedDeviceId(value, role);
      const scanned = resolved.ok ? resolved.deviceId : value;
      if (!resolved.ok && looksLikeRacerangerDeviceScanAttempt(value)) {
        dispatchToast('toast-error', NOT_RACERANGER_DEVICE_SCAN_TOAST);
        return;
      }
      if (role === 'rear' && !isRearDeviceId(scanned)) {
        dispatchToast('toast-error', 'Rear device scan must be a Rear_ device.');
        return;
      }
      if (role === 'front' && !isFrontDeviceId(scanned)) {
        dispatchToast('toast-error', 'Front device scan must be a Front_ device.');
        return;
      }

      if (addAthletePhase === 'rear') {
        setNewAthleteRear(scanned);
        setAddAthletePhase('front');
      } else {
        setNewAthleteFront(scanned);
      }
      setAddAthleteScanValue('');
    },
    [addAthletePhase],
  );

  const { connectionActive: addAthleteScannerActive, claim: claimAddAthleteScanner } = useBarcodeScanner({
    enabled: showAddAthlete,
    onScan: (value) => {
      setAddAthleteScanValue(value);
      applyAddAthleteScan(value);
    },
  });

  const submitNewAthlete = useCallback(async (): Promise<void> => {
    const bib = newAthleteBib.trim();
    const name = newAthleteName.trim();
    const rear = maybeConvertRacerangerScan(newAthleteRear).trim();
    const front = maybeConvertRacerangerScan(newAthleteFront).trim();
    if (!bib) {
      dispatchToast('toast-error', 'Bib number is required.');
      return;
    }
    if (!name) {
      dispatchToast('toast-error', 'Athlete name is required.');
      return;
    }
    if (!rear) {
      dispatchToast('toast-error', 'Rear device ID is required.');
      return;
    }
    const masterSet = masterIds === undefined ? await loadMasterIds() : masterIds;
    const check = validateRearDeviceMappingAndFormat(rear, masterSet ?? null);
    if (!check.ok) {
      dispatchToast('toast-error', check.reason ?? 'Rear device validation failed');
      return;
    }
    if (!window.eventsAPI?.addRaceAthleteWithDevices) {
      dispatchToast('toast-error', 'Add athlete API unavailable.');
      return;
    }

    setAddingAthlete(true);
    try {
      const res = await window.eventsAPI.addRaceAthleteWithDevices(eventId, raceId, {
        athleteName: name,
        athleteRaceNo: bib,
        rearDeviceId: rear,
        frontDeviceId: front || undefined,
      });
      if (!res?.ok) {
        const rawError = String(res?.error ?? '');
        dispatchToast(
          'toast-error',
          rawError.includes('race_no_already_assigned')
            ? `Bib ${bib} is already assigned to another athlete on this race.`
            : rawError.includes('athlete_already_exists')
              ? 'That athlete already exists on this race.'
              : rawError.includes('device_assignment_conflict') || rawError.includes('device_already_assigned')
                ? 'One of those devices is already assigned elsewhere.'
                : res?.error || 'Failed to add athlete.',
        );
        return;
      }
      dispatchToast('toast-info', `Added ${name} (bib ${bib}).`);
      resetAddAthleteFields();
      await Promise.resolve(onRefresh?.(raceId));
    } finally {
      setAddingAthlete(false);
    }
  }, [newAthleteBib, newAthleteName, newAthleteRear, newAthleteFront, masterIds, loadMasterIds, eventId, raceId, onRefresh]);

  // Staged (non-combined) races only ever run one of collection/racking at a time —
  // whichever matches the race's current status — so there's no tab to pick from.
  // Combined-mode races run both concurrently during DISTRIBUTION, so the operator
  // picks via the toggle below.
  const effectiveScanTarget: ScanTarget = combinedMode
    ? scanTarget
    : raceStatus === 'RACKING'
      ? 'racking'
      : 'collection';
  const showCollectedColumn = combinedMode || effectiveScanTarget === 'collection';
  const showRackedColumn = combinedMode || effectiveScanTarget === 'racking';

  const statusHint =
    effectiveScanTarget === 'collection'
      ? 'Scan the bib QR code on the packet to mark that athlete collected.'
      : rackingPhase === 'bib'
        ? 'Scan the athlete bib number to select them.'
        : rackingPhase === 'rear'
          ? `Bib ${activeRaceNo} selected — now scan the REAR device.`
          : `Bib ${activeRaceNo} — rear verified — now scan the FRONT device.`;

  const scannerPlaceholder =
    effectiveScanTarget === 'collection'
      ? { scan: 'Scan bib QR code...', manual: 'Type bib number and press Enter...' }
      : rackingPhase === 'bib'
        ? { scan: 'Scan athlete bib number...', manual: 'Type bib number and press Enter...' }
        : { scan: `Scan ${rackingPhase} device...`, manual: `Type ${rackingPhase} device ID and press Enter...` };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 bg-gray-50 px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-800">
          <span className="font-semibold text-gray-800 dark:text-slate-200">Athletes</span>
          <div className="flex items-center gap-3">
            <span className="text-gray-500 dark:text-slate-400 tabular-nums">
              {collectedCount}/{rows.length} collected, {rackedCount}/{rows.length} racked
            </span>
            <button
              type="button"
              onClick={() => {
                if (showAddAthlete) {
                  cancelAddAthlete();
                  return;
                }
                setShowAddAthlete(true);
                claimAddAthleteScanner();
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {showAddAthlete ? 'Cancel' : '+ Add athlete'}
            </button>
          </div>
        </div>
        {showAddAthlete ? (
          <div className="border-b border-slate-200 bg-blue-50/40 px-3 py-2.5 dark:border-slate-700 dark:bg-blue-950/20">
            <p className="mb-2 text-[11px] text-gray-500 dark:text-slate-400">
              Add a walk-up athlete and assign their devices — works even after packets have been packed and the race has moved to {raceStatus === 'RACKING' ? 'racking' : 'distribution'}.
            </p>
            <input
              className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900"
              placeholder="Athlete name"
              value={newAthleteName}
              onChange={(e) => setNewAthleteName(e.target.value)}
              disabled={addingAthlete}
            />

            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] text-gray-500 dark:text-slate-400">Scan bib, then rear, then front</span>
              <ScanStepBreadcrumb steps={RACKING_STEPS} currentKey={addAthletePhase} />
            </div>
            <BarcodeScannerEntryField
              connected={addAthleteScannerActive}
              value={addAthleteScanValue}
              onChange={setAddAthleteScanValue}
              onSubmit={applyAddAthleteScan}
              mode={addAthleteScanMode}
              onModeChange={setAddAthleteScanMode}
              placeholderScan={
                addAthletePhase === 'bib'
                  ? 'Scan athlete bib number...'
                  : `Scan ${addAthletePhase} device...`
              }
              placeholderManual={
                addAthletePhase === 'bib'
                  ? 'Type bib number and press Enter...'
                  : `Type ${addAthletePhase} device ID and press Enter...`
              }
              placeholderDisconnected="Focus to connect scanner"
              disabled={addingAthlete}
              onFocus={claimAddAthleteScanner}
            />

            <div className="mt-2 grid grid-cols-3 gap-2">
              <input
                className={`rounded border px-2 py-1 text-xs dark:bg-slate-900 ${
                  addAthletePhase === 'bib'
                    ? 'border-blue-400 ring-1 ring-blue-400/60 dark:border-blue-500'
                    : 'border-gray-300 dark:border-slate-600'
                }`}
                placeholder="1 Bib #"
                value={newAthleteBib}
                onChange={(e) => setNewAthleteBib(e.target.value)}
                disabled={addingAthlete}
              />
              <input
                className={`rounded border px-2 py-1 font-mono text-xs dark:bg-slate-900 ${
                  addAthletePhase === 'rear'
                    ? 'border-blue-400 ring-1 ring-blue-400/60 dark:border-blue-500'
                    : 'border-gray-300 dark:border-slate-600'
                }`}
                placeholder="2 Rear device (Rear_...)"
                value={newAthleteRear}
                onChange={(e) => setNewAthleteRear(e.target.value)}
                disabled={addingAthlete}
              />
              <input
                className={`rounded border px-2 py-1 font-mono text-xs dark:bg-slate-900 ${
                  addAthletePhase === 'front'
                    ? 'border-blue-400 ring-1 ring-blue-400/60 dark:border-blue-500'
                    : 'border-gray-300 dark:border-slate-600'
                }`}
                placeholder="3 Front device (optional)"
                value={newAthleteFront}
                onChange={(e) => setNewAthleteFront(e.target.value)}
                disabled={addingAthlete}
              />
            </div>
            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={cancelAddAthlete}
                disabled={addingAthlete}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitNewAthlete()}
                disabled={addingAthlete}
                className="rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-900 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100"
              >
                {addingAthlete ? 'Adding…' : 'Add athlete'}
              </button>
            </div>
          </div>
        ) : null}
        {rows.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">No devices assigned to this race.</div>
        ) : (
          <div className="max-h-[26rem] overflow-auto">
            <table ref={tableRef} className="w-full text-left text-xs">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-slate-800">
                <tr className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  <th className="px-3 py-2">Bib</th>
                  <th className="px-3 py-2">Athlete</th>
                  <th className="px-3 py-2">Rear device</th>
                  <th className="px-3 py-2">Front device</th>
                  {showCollectedColumn ? <th className="px-3 py-2 text-center">Collected</th> : null}
                  {showRackedColumn ? <th className="px-3 py-2 text-center">Racked</th> : null}
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {rows.map((row, rowIndex) => {
                  const key = row.athleteId || row.rearDeviceId;
                  const editing = editingAthleteId === row.athleteId && !!row.athleteId;
                  const isScanTarget = highlightedRowKey === key;
                  const mismatch = mismatchByAthleteId[row.athleteId];
                  const cellKey = row.athleteId || row.rearDeviceId;
                  const collectedPending = pendingCell?.key === cellKey && pendingCell.field === 'collected';
                  const rackedPending = pendingCell?.key === cellKey && pendingCell.field === 'racked';
                  return (
                    <tr
                      key={key}
                      data-row-index={rowIndex}
                      className={isScanTarget ? 'bg-blue-50/70 dark:bg-blue-950/30 ring-1 ring-inset ring-blue-300 dark:ring-blue-600/60' : undefined}
                    >
                      <td className="px-3 py-2 text-gray-700 dark:text-slate-300 tabular-nums">{row.athleteRaceNo || '—'}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-slate-100">{row.athleteName || (row.athleteId ? row.athleteId : '—')}</td>
                      {editing ? (
                        <>
                          <td className="px-3 py-2">
                            <input
                              className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs dark:border-slate-600 dark:bg-slate-900"
                              value={editRear}
                              onChange={(e) => setEditRear(e.target.value)}
                              disabled={savingEdit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              className="w-full rounded border border-gray-300 px-1.5 py-1 font-mono text-xs dark:border-slate-600 dark:bg-slate-900"
                              value={editFront}
                              onChange={(e) => setEditFront(e.target.value)}
                              disabled={savingEdit}
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2">
                            <span className={row.rearRacked ? RACKED_CELL : PLAIN_CELL}>{row.rearDeviceId || '—'}</span>
                            {mismatch?.rearMatches === false ? (
                              <span className="ml-1.5 text-[10px] font-semibold text-red-700 dark:text-red-400">mismatch</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2">
                            <span className={row.frontRacked ? RACKED_CELL : PLAIN_CELL}>{row.frontDeviceId || '—'}</span>
                            {mismatch?.frontMatches === false ? (
                              <span className="ml-1.5 text-[10px] font-semibold text-red-700 dark:text-red-400">mismatch</span>
                            ) : null}
                          </td>
                        </>
                      )}
                      {showCollectedColumn ? (
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <TickOrCross ok={row.collected} loading={collectedPending} />
                            {row.collected ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => void undoCollection(row)}
                                title={
                                  row.rearRacked || row.frontRacked
                                    ? 'Un-rack this athlete first, then undo collection.'
                                    : undefined
                                }
                                className="text-[10px] text-gray-500 underline hover:text-gray-700 disabled:opacity-50 dark:text-slate-400 dark:hover:text-slate-200"
                              >
                                undo
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                      {showRackedColumn ? (
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <TickOrCross ok={row.racked} loading={rackedPending} />
                            {row.rearRacked || row.frontRacked ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => void undoRacking(row)}
                                className="text-[10px] text-gray-500 underline hover:text-gray-700 disabled:opacity-50 dark:text-slate-400 dark:hover:text-slate-200"
                              >
                                undo
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                      <td className="px-3 py-2 text-right">
                        {editing ? (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={savingEdit}
                              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void saveEdit()}
                              disabled={savingEdit}
                              className="rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-900 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100"
                            >
                              {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => beginEdit(row)}
                            disabled={!canEdit || !row.athleteId}
                            title={
                              !row.athleteId
                                ? 'No athlete assigned to this device.'
                                : !canEdit
                                  ? 'Device swaps are available once devices are being racked (or during Distribution for combined-mode races).'
                                  : undefined
                            }
                            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        {combinedMode ? (
          // Unambiguous mode switch — each option always shows what it does, not just on hover/active.
          // Staged (non-combined) races never show this: the race status alone decides which mode applies.
          <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
            <button
              type="button"
              onClick={() => {
                setScanTarget('collection');
                resetRackingScan();
                setScanValue('');
                claimScanner();
              }}
              className={[
                'flex flex-col items-center gap-0.5 border-b-2 px-3 py-1.5 text-center transition-colors',
                scanTarget === 'collection'
                  ? 'border-blue-500 bg-blue-50/70 text-blue-700 dark:border-blue-500 dark:bg-blue-950/30 dark:text-blue-300'
                  : 'border-transparent bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
              ].join(' ')}
              aria-pressed={scanTarget === 'collection'}
            >
              <PackageCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
              <span className="text-[11px] font-semibold uppercase tracking-wide">Collection</span>
              <span className={scanTarget === 'collection' ? 'text-[10px] text-blue-600/70 dark:text-blue-300/70' : 'text-[10px] text-gray-400 dark:text-slate-500'}>
                Scan bib QR to mark collected
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setScanTarget('racking');
                resetRackingScan();
                setScanValue('');
                claimScanner();
              }}
              className={[
                'flex flex-col items-center gap-0.5 border-b-2 px-3 py-1.5 text-center transition-colors',
                scanTarget === 'racking'
                  ? 'border-cyan-500 bg-cyan-50/70 text-cyan-700 dark:border-cyan-500 dark:bg-cyan-950/30 dark:text-cyan-300'
                  : 'border-transparent bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
              ].join(' ')}
              aria-pressed={scanTarget === 'racking'}
            >
              <ClipboardCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
              <span className="text-[11px] font-semibold uppercase tracking-wide">Racking</span>
              <span className={scanTarget === 'racking' ? 'text-[10px] text-cyan-600/70 dark:text-cyan-300/70' : 'text-[10px] text-gray-400 dark:text-slate-500'}>
                Scan bib, then rear, then front
              </span>
            </button>
          </div>
        ) : null}

        <div className="px-3 py-2 text-xs">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-gray-600 dark:text-slate-300">{statusHint}</span>
            {effectiveScanTarget === 'racking' ? (
              <ScanStepBreadcrumb steps={RACKING_STEPS} currentKey={rackingPhase} />
            ) : null}
          </div>
          <BarcodeScannerEntryField
            connected={connectionActive}
            value={scanValue}
            onChange={(value) => {
              setScanValue(value);
            }}
            onSubmit={(value) => {
              void (effectiveScanTarget === 'collection' ? applyCollectionScan(value) : applyRackingScan(value));
            }}
            mode={scanMode}
            onModeChange={setScanMode}
            placeholderScan={scannerPlaceholder.scan}
            placeholderManual={scannerPlaceholder.manual}
            placeholderDisconnected="Focus to connect scanner"
            disabled={busy}
            loading={busy}
            onFocus={claimScanner}
          />
        </div>
      </div>
    </div>
  );
}
